import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { authService } from '../services/auth'
import { profileService } from '../services/profile'
import { setCallerSellerId } from '../crm/services/api'
import pageCache, { navState } from '../crm/services/pageCache'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [initializing, setInitializing] = useState(true)

  useEffect(() => {
    // onAuthStateChange emite INITIAL_SESSION imediatamente ao se inscrever —
    // nascente única da sessão. init()/getSession() separado criava dois rios
    // disputando setUser e podia deixar initializing=true para sempre se falhasse.
    //
    // O CALLBACK NÃO PODE SER `async` NEM CHAMAR O SUPABASE
    // ─────────────────────────────────────────────────────
    // O supabase-js invoca este callback DE DENTRO do lock
    // `lock:sb-<ref>-auth-token` (Web Locks API). Qualquer chamada ao banco
    // pelo mesmo cliente precisa do access token e tenta adquirir esse mesmo
    // lock — que não é reentrante. Resultado medido em 19/08/2026:
    //
    //   navigator.locks.query() → held: ['lock:sb-leiqrjctwbqqraahmxib-auth-token']
    //
    // O `await profileService.getProfile(...)` que ficava aqui dentro travava
    // esperando o lock que o próprio callback segurava. O callback nunca
    // retornava, o lock nunca era liberado, e as duas telas congelavam:
    //   · `initializing` nunca virava false  → PrivateRoute em "Carregando..."
    //   · signInWithPassword ficava na fila  → botão em "Entrando..."
    // Um deadlock, dois sintomas. É o anti-padrão que a doc do Supabase
    // adverte: nunca chamar funções do Supabase dentro deste callback.
    //
    // A saída é devolver o controle ao supabase-js ANTES de tocar no banco.
    // O callback é síncrono e retorna na hora, liberando o lock; o perfil é
    // buscado em `setTimeout(…, 0)`, já fora dele.
    //
    // A ORDEM FOI PRESERVADA DE PROPÓSITO
    // ───────────────────────────────────
    // `setInitializing(false)` continua acontecendo DEPOIS do perfil resolver.
    // Abrir o portão antes seria mais simples e estaria errado: `PrivateRoute`
    // liberaria `/app` com `profile` ainda nulo, e `SellerDashboard` (l. 30, 36)
    // deriva `seller_id` e `is_admin` dele. Pior, `setCallerSellerId` (efeito
    // abaixo) alimenta o header `X-Caller-Seller-Id` — a barreira de privacidade
    // do backend —, e `_buildHeaders` OMITE o header quando o valor é nulo.
    // Uma requisição disparada nessa janela sairia sem a barreira. O invariante
    // "initializing false ⇒ perfil resolvido" continua valendo.
    const { data: { subscription } } = authService.onAuthStateChange(
      (_event, session) => {
        const currentUser = session?.user || null
        setUser(currentUser)

        if (!currentUser) {
          setProfile(null)
          setInitializing(false) // 1ª emissão (INITIAL_SESSION) abre o portão
          return
        }

        // Fora do lock — tarefa nova, pilha nova.
        setTimeout(async () => {
          try {
            const userProfile = await profileService.getProfile(currentUser.id)
            setProfile(userProfile)
          } catch {
            setProfile(null)
          } finally {
            setInitializing(false) // o portão abre com o perfil já resolvido
          }
        }, 0)
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const login = useCallback(async (email, password) => {
    setError('')
    setLoading(true)

    try {
      await authService.signIn(email, password)
      // onAuthStateChange (SIGNED_IN) cuida de setUser + setProfile.
      // login() tem uma responsabilidade: autenticar e navegar.
      navigate('/app')
    } catch (err) {
      setError(err.message === 'Invalid login credentials'
        ? 'Email ou senha incorretos'
        : err.message)
    } finally {
      setLoading(false)
    }
  }, [navigate])

  const logout = useCallback(async () => {
    // Limpa o cache de página antes de deslogar.
    // Garante que dados do usuário atual não vazem para o próximo
    // usuário que logar no mesmo browser/computador.
    pageCache.clear()
    navState.clear(profile?.seller_id)
    await authService.signOut()
    setProfile(null)
    navigate('/login')
  }, [navigate])

  const isAuthenticated = useCallback(() => {
    return !!user
  }, [user])

  // Sincroniza identidade do caller para o api.js (regra de privacidade no backend).
  useEffect(() => {
    setCallerSellerId(profile?.seller_id || null)
  }, [profile?.seller_id])

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      login,
      logout,
      isAuthenticated,
      loading,
      error,
      initializing,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>')
  return ctx
}
